import { Flex, TileGroup, VStack, Box, InlineBanner, Text } from '@cad-arc';
import { LoadingIndicator } from '../../../common/components/loading-indicator';
import { useIntegratedForm } from '../../../forms/use-integrated-form';
import { useSearchUnitHistoryResponse } from '../../context/hooks';
import { useIsUnitHistorySearchEmpty } from '../../hooks/use-is-unit-history-search-empty';
import { UnitHistoryForm } from '../../types';
import { IncludedEventTile } from './included-event-tile';

export function IncludedEvents({
    resultsScrollElementRef,
}: {
    resultsScrollElementRef: React.RefObject<HTMLDivElement>;
}): React.ReactElement {
    return (
        <VStack gap="4px" align="left" h="100%" overflowY="auto">
            <Text variant="headingXs" color="secondary" style={{ paddingLeft: '16px' }}>
                Included Events
            </Text>
            <IncludedEventsBody resultsScrollElementRef={resultsScrollElementRef} />
        </VStack>
    );
}

function IncludedEventsBody({
    resultsScrollElementRef,
}: {
    resultsScrollElementRef: React.RefObject<HTMLDivElement>;
}): React.ReactElement {
    const searchUnitHistoryResponse = useSearchUnitHistoryResponse();
    const includedEvents = searchUnitHistoryResponse?.includedEvents ?? [];
    const unitHistoryItemsGroups = searchUnitHistoryResponse?.unitHistoryItemsGroups ?? [];
    const hasUnitHistoryResults = unitHistoryItemsGroups.length;
    const { form, mutation } = useIntegratedForm<UnitHistoryForm>();
    const isSearchEmpty = useIsUnitHistorySearchEmpty();

    const scrollToEventInResults = (eventId: number): void => {
        const resultGroupIndex = unitHistoryItemsGroups.findIndex(
            (group) => !!group.events.find((event) => event.id === eventId)
        );
        if (resultsScrollElementRef.current) {
            const itemToScrollTo = resultsScrollElementRef.current.querySelector(
                `[data-index="${resultGroupIndex}"]`
            );
            if (itemToScrollTo) {
                itemToScrollTo.scrollIntoView({
                    behavior: 'smooth',
                    block: 'nearest',
                });
            }
        }
    };

    if (mutation.isError) {
        return (
            <InlineBanner
                status="error"
                title="An error occurred while loading events"
                actions={[{ children: 'Retry', onClick: () => mutation.mutate(form.getValues()) }]}
            />
        );
    }
    if (mutation.isLoading) {
        return (
            <Box w="100%" h="100%" padding="16px">
                <LoadingIndicator />
            </Box>
        );
    }
    if (isSearchEmpty) {
        return (
            <Flex h="100%" w="100%" justifyContent="center" alignItems="flex-start">
                <VStack>
                    <Text variant="headingXs">No Results</Text>
                    <Text variant="bodyMd">Search to get started</Text>
                </VStack>
            </Flex>
        );
    }
    if (includedEvents.length === 0) {
        if (hasUnitHistoryResults) {
            return (
                <Flex h="100%" w="100%" justifyContent="center" alignItems="flex-start">
                    <Text variant="headingXs">No events included in this search</Text>
                </Flex>
            );
        } else {
            return (
                <Flex h="100%" w="100%" justifyContent="center" alignItems="flex-start">
                    <Text variant="headingXs">No Results</Text>
                </Flex>
            );
        }
    }
    return (
        <Box w="100%" h="100%" overflowY="auto" padding="3px 16px 8px 16px">
            <TileGroup style={{ width: '100%' }} gap="8px">
                {includedEvents.map((event) => (
                    <IncludedEventTile
                        key={event.id}
                        event={event}
                        onClick={() => scrollToEventInResults(event.id)}
                    />
                ))}
            </TileGroup>
        </Box>
    );
}
